import React from "react";
import { useStaffTraining } from '../context/StaffTrainingContext';
import { trainingDate, courseLabel } from '../trainingUi';
import TrainingRecordActions from './TrainingRecordActions';
import {
  FiSearch,
  FiChevronDown,
  FiChevronLeft,
  FiChevronRight,
} from "react-icons/fi";
import {
  HiOutlineCheckCircle,
  HiOutlineClock,
  HiOutlineExclamationCircle,
} from "react-icons/hi2";

export default function TrainingComplianceTable() {
  const { visibleRows, data, search, setSearch, status, setStatus, category, setCategory, rows, page, setPage, pageCount, emptyMessage, canEdit } = useStaffTraining();
  const cellData = cell => !cell?.recordId ? { status: 'none', label: 'Missing', date: '—' } : ({
    status: cell.status === 'Overdue' ? 'danger' : cell.status === 'Due soon' ? 'warning' : cell.status === 'Recorded' ? 'recorded' : 'success',
    label: cell.status, date: trainingDate(cell.trainedOn),
    expiry: cell.expiresOn ? 'Expires ' + trainingDate(cell.expiresOn) : 'Expiry not set',
  });
  const staffData = visibleRows.map(person => ({ ...person, name: person.fullName,
    initials: person.fullName.split(' ').map(word => word[0]).slice(0, 2).join(''),
    foodSafety: cellData(person.categories.find(cell => cell.code === 'food-safety')),
    allergens: cellData(person.categories.find(cell => cell.code === 'allergens')),
    healthSafety: cellData(person.categories.find(cell => cell.code === 'health-safety-induction')),
    fireSafety: cellData(person.categories.find(cell => cell.code === 'fire-safety')),
  }));

  // Helper component to render specific cell configurations cleanly
  const TrainingCell = ({ data }) => {
    if (!data || data.status === "none") {
      return (
        <div className="flex flex-col text-left">
          <span className="text-xs font-semibold text-slate-400">
            {data?.label || "N/A"}
          </span>
          <span className="text-[10px] text-slate-400">—</span>
        </div>
      );
    }

    return (
      <div className="flex items-start gap-2 text-left">
        <div className="mt-0.5 shrink-0">
          {data.status === "success" && (
            <HiOutlineCheckCircle className="w-4 h-4 text-emerald-600" />
          )}
          {data.status === "warning" && (
            <HiOutlineClock className="w-4 h-4 text-amber-500" />
          )}
          {data.status === "danger" && (
            <HiOutlineExclamationCircle className="w-4 h-4 text-rose-600" />
          )}
        </div>
        <div className="flex flex-col min-w-0">
          <span
            className={`text-xs font-semibold ${data.status === "danger" ? "text-rose-600" : data.status === "warning" ? "text-amber-600" : "text-slate-700"} truncate`}
          >
            {data.title || data.label || (data.status === "success" && "")}
          </span>
          <span className="text-[10px] text-slate-400 font-medium whitespace-nowrap">
            {data.date}
          </span>{data.expiry && <span className="mt-0.5 text-[9px] text-slate-400">{data.expiry}</span>}
        </div>
      </div>
    );
  };

  // Helper to resolve styles for the master "Overall Status" badge block
  const getOverallStatusStyles = (status) => {
    switch (status) {
      case "Up to date":
        return "bg-emerald-50 text-emerald-700 border border-emerald-100";
      case "Due soon":
        return "bg-amber-50 text-amber-700 border border-amber-100";
      case "Overdue":
        return "bg-rose-50 text-rose-700 border border-rose-100";
      default:
        return "bg-slate-50 text-slate-600";
    }
  };

  return (
    <div className="w-full bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden font-sans">
      {/* Top Header Filter Control Area */}
      <div className="p-5 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between border-b border-slate-100">
        <div>
          <h2 className="text-lg font-bold text-slate-900 tracking-tight">
            Training Compliance by Staff
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Shows completed training and current status for each staff member.
          </p>
        </div>

        {/* Input Controls Container Row */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative">
            <select aria-label="Training category filter" value={category} onChange={event => setCategory(event.target.value)} className="appearance-none bg-white border border-slate-200 rounded-lg pl-3 pr-8 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 focus:outline-none min-w-[110px]">
              <option value="">All Categories</option>{data?.categories.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
            </select>
            <FiChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          </div>

          <div className="relative">
            <select aria-label="Training status filter" value={status} onChange={event => setStatus(event.target.value)} className="appearance-none bg-white border border-slate-200 rounded-lg pl-3 pr-8 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 focus:outline-none min-w-[120px]">
              <option value="">All Statuses</option>{["Up to date", "Recorded", "Due soon", "Overdue", "Training gaps"].map(value => <option key={value}>{value}</option>)}
            </select>
            <FiChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          </div>

          <div className="relative min-w-[180px]">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-3.5 h-3.5" />
            <input
              type="text"
              placeholder="Search staff..." value={search} onChange={event => setSearch(event.target.value)}
              className="w-full pl-9 pr-3 py-1.5 bg-white border border-slate-200 rounded-lg text-xs placeholder-slate-400 text-slate-700 focus:outline-none focus:border-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Responsive Horizontal Scroll wrapper */}
      <div className="w-full overflow-x-auto">
        <table className="w-full border-collapse text-left min-w-[800px]">
          <thead>
            <tr className="border-b border-slate-100 bg-slate-50/50">
              <th className="py-3 px-4 text-xs font-semibold text-slate-500 tracking-wide w-[200px]">
                Staff Member
              </th>
              <th className="py-3 px-4 text-xs font-semibold text-slate-500 tracking-wide">
                Level
              </th>
              <th className="py-3 px-4 text-xs font-semibold text-slate-500 tracking-wide">
                Food Safety
              </th>
              <th className="py-3 px-4 text-xs font-semibold text-slate-500 tracking-wide">
                Allergens
              </th>
              <th className="py-3 px-4 text-xs font-semibold text-slate-500 tracking-wide">
                Health & Safety Induction
              </th>
              <th className="py-3 px-4 text-xs font-semibold text-slate-500 tracking-wide">
                Fire Safety
              </th>
              <th className="py-3 px-4 text-xs font-semibold text-slate-500 tracking-wide text-center w-[110px]">
                Overall Status
              </th>
              <th className="py-3 px-4 text-xs font-semibold text-slate-500 tracking-wide text-center w-[60px]">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {staffData.map(row => (
              <tr
                key={row.id}
                className="hover:bg-slate-50/50 transition-colors"
              >
                {/* Profile Circle and User Title Meta column */}
                <td className="py-3.5 px-4">
                  <div className="flex items-center gap-3">
                    <div className="w-7 h-7 bg-slate-100 rounded-full flex items-center justify-center shrink-0">
                      <span className="text-[10px] font-bold text-slate-500 tracking-wider">
                        {row.initials}
                      </span>
                    </div>
                    <span className="text-xs font-bold text-slate-800 whitespace-nowrap">
                      {row.name}
                    </span>
                  </div>
                </td>

                <td className="py-3.5 px-4 text-xs font-semibold text-slate-700 whitespace-nowrap">
                  {courseLabel(row.courseLevel || row.categories.find(cell => cell.courseLevel)?.courseLevel)}
                </td>

                {/* Individual Module Columns map implementation */}
                <td className="py-3.5 px-4">
                  <TrainingCell data={row.foodSafety} />
                </td>
                <td className="py-3.5 px-4">
                  <TrainingCell data={row.allergens} />
                </td>
                <td className="py-3.5 px-4">
                  <TrainingCell data={row.healthSafety} />
                </td>
                <td className="py-3.5 px-4">
                  <TrainingCell data={row.fireSafety} />
                </td>

                {/* Status indicator module mapping placement block */}
                <td className="py-3.5 px-4 text-center">
                  <span
                    className={`inline-block px-2.5 py-0.5 text-[10px] font-semibold rounded-full whitespace-nowrap capitalize ${getOverallStatusStyles(row.overallStatus)}`}
                  >
                    {row.overallStatus}
                  </span>
                </td>

                {/* Dynamic Context Menu Node Trigger button icon */}
                <td className="py-3.5 px-4 text-center">
                  <TrainingRecordActions staff={row} canEdit={canEdit} />
                </td>
              </tr>
            ))}
            {!staffData.length && <tr><td colSpan={8} className="p-6 text-center text-sm text-slate-500">{emptyMessage}</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Footer Navigation Bar Markup Layout block area */}
      <div className="px-5 py-3.5 bg-white border-t border-slate-100 flex items-center justify-between">
        <span className="text-xs font-medium text-slate-500">
          Showing {rows.length ? (page - 1) * 8 + 1 : 0} to {Math.min(page * 8, rows.length)} of {rows.length} staff
        </span>

        {/* Pagination Action Triggers menu wrapper block */}
        <div className="flex items-center gap-1.5">
          <button type="button" aria-label="Previous page" disabled={page <= 1} onClick={() => setPage(page - 1)} className="p-1.5 border border-slate-200 rounded-lg text-slate-400 bg-white hover:bg-slate-50 disabled:opacity-50 transition">
            <FiChevronLeft className="w-4 h-4" />
          </button>

          {Array.from({ length: pageCount }, (_, index) => index + 1).map(number => (
            <button key={number} type="button" aria-label={'Page ' + number} aria-current={number === page ? 'page' : undefined}
              onClick={() => setPage(number)}
              className={'w-7 h-7 flex items-center justify-center rounded-lg text-xs font-semibold ' + (number === page
                ? 'bg-blue-600 text-white shadow-sm'
                : 'border border-slate-200 text-slate-600 bg-white hover:bg-slate-50 transition')}>
              {number}
            </button>
          ))}

          <button type="button" aria-label="Next page" disabled={page >= pageCount} onClick={() => setPage(page + 1)} className="p-1.5 border border-slate-200 rounded-lg text-slate-400 bg-white hover:bg-slate-50 transition">
            <FiChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
